<template>
  <section class="ma-4 ma-md-8">
    <t-page-title :title="`Vendas | Relatório de Compras | ${order?.event_name ?? ''}`" />
    <v-row>
      <v-col>
        <v-card :loading="loading" class="pa-8">
          <v-card-title>
            <v-card-actions v-if="!loader.loading">
              <span class="font-weight-bold">
                {{ order?.order_number ?? '' }}
              </span>

              <v-spacer />
              <v-btn
                v-if="order?.form_data"
                color="info"
                variant="flat"
                @click="showDialog = !showDialog"
              >
                Dados Adquirente
              </v-btn>
              <v-btn
                v-if="order?.status !== 'Cancelado'"
                color="error"
                variant="flat"
                @click="cancel"
              >
                Cancelar
              </v-btn>
              <v-btn
                color="error"
                :disabled="refunded || ![1, 5].includes(order?.payment_method?.id!)"
                variant="flat"
                @click="void_"
              >
                {{ refunded ? 'Estornado' : 'Estornar' }}
              </v-btn>
              <v-btn
                color="error"
                :disabled="!!order?.chargebacked_at"
                variant="flat"
                @click="chargeback"
              >
                Chargeback
              </v-btn>
              <v-btn
                color="success"
                :disabled="order?.status !== 'Confirmado'"
                variant="flat"
                @click="print"
              >
                Voucher
              </v-btn>
              <v-btn
                color="success"
                variant="flat"
                @click="login"
              >
                Logar
              </v-btn>
            </v-card-actions>
          </v-card-title>

          <v-card-text v-if="!loading">
            <v-divider class="my-2" />

            <v-table>
              <tbody>
                <tr v-for="item in orderData" :key="item.value">
                  <td class="text-left">
                    <span class="font-weight-bold">{{ item.text }}:</span>
                  </td>
                  <td class="text-right">
                    {{ item.value }}
                  </td>
                </tr>
              </tbody>
            </v-table>

            <v-divider class="my-2" />

            <div class="my-6">
              <span class="text-h6 font-weight-medium"> Comprador</span>
            </div>

            <v-divider class="my-2" />

            <v-table>
              <tbody>
                <tr v-for="item in ownerData" :key="item.value">
                  <td class="text-left">
                    <span class="font-weight-bold">{{ item.text }}:</span>
                  </td>
                  <td class="text-right">
                    {{ item.value }}
                  </td>
                </tr>
              </tbody>
            </v-table>

            <v-divider class="my-2" />

            <div class="my-6">
              <span class="text-h6 font-weight-medium"> Itens</span>
            </div>

            <v-divider class="my-2" />

            <v-table>
              <thead>
                <tr>
                  <th class="font-weight-bold text-left">
                    Seção
                  </th>
                  <th class="font-weight-bold text-left">
                    Setor
                  </th>
                  <th class="font-weight-bold text-left">
                    Cadeira
                  </th>
                  <th class="font-weight-bold text-left">
                    Tipo
                  </th>
                  <th class="font-weight-bold text-left">
                    Taxa de Serviço
                  </th>
                  <th class="font-weight-bold text-left">
                    Valor
                  </th>
                  <th class="font-weight-bold text-left">
                    Qnt.
                  </th>
                  <th class="font-weight-bold text-left">
                    Valor Total
                  </th>
                  <th class="font-weight-bold text-left">
                    Horário da Leitura
                  </th>
                  <th v-if="can('update', 'holders') || can('update', 'ticket-type')" class="font-weight-bold text-left">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-for="item in order?.items" :key="item.id">
                  <tr>
                    <td class="text-left">
                      {{ item.section }}
                    </td>
                    <td class="text-left">
                      {{ item.sector }}
                    </td>
                    <td class="text-left">
                      {{ item.seat ?? 'Arquibancada única' }}
                    </td>
                    <td class="text-left">
                      {{ item.type }}
                    </td>
                    <td class="text-left">
                      {{ currency(item.fee) }}
                    </td>
                    <td class="text-left">
                      {{ currency(item.value) }}
                    </td>
                    <td class="text-left">
                      {{ item.quantity }}
                    </td>
                    <td class="text-left">
                      {{ currency(item.fee + item.value) }}
                    </td>
                    <td class="text-left">
                      {{ getReadingDate(item.passport?.reading) }}
                    </td>
                    <td v-if="can('update', 'holders') || can('update', 'ticket-type')" class="text-right">
                      <div class="d-flex">
                        <v-btn
                          v-if="can('update', 'holders')"
                          icon
                          :disabled="loader.loading"
                          density="compact"
                          min-height="32"
                          min-width="32"
                          elevation="1"
                          class="mr-2 pa-2"
                          @click="toggleHolderForm(item)"
                        >
                          <v-icon color="info" icon="fa-solid fa-pen" size="x-small" />
                        </v-btn>
                        <v-btn
                          v-if="can('update', 'ticket-type')"
                          icon
                          :disabled="loader.loading || !!item.passport?.reading"
                          density="compact"
                          min-height="32"
                          min-width="32"
                          elevation="1"
                          class="mr-2 pa-2"
                          @click="typeChangeItem = item"
                        >
                          <v-icon color="info" icon="fa-solid fa-right-left" size="x-small" />
                        </v-btn>
                      </div>
                    </td>
                  </tr>
                  <template v-if="expanded(item.id)">
                    <tr>
                      <td class="text-left" colspan="2">
                        <span class="pl-3 font-weight-bold">Nome do Portador:</span>
                      </td>
                      <td class="text-left" colspan="8">
                        <v-text-field
                          v-model="item.name"
                          :rules="[ v => !!v && v.trim().length >= 3 || 'O nome deve ter ao menos 3 caracteres.']"
                          variant="outlined"
                          density="compact"
                          hide-details
                        />
                      </td>
                    </tr>
                    <tr>
                      <td class="text-left" colspan="2">
                        <span class="pl-3 font-weight-bold">Tipo de Documento:</span>
                      </td>
                      <td class="text-left" colspan="8">
                        <v-select
                          v-model="item.document_type"
                          :items="[
                            { title: 'CPF', value: 'cpf' },
                            { title: 'Passaporte', value: 'passport' },
                          ]"
                          variant="outlined"
                          density="compact"
                          hide-details
                          @update:model-value="item.document = ''"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td class="text-left" colspan="2">
                        <span class="pl-3 font-weight-bold">Documento do Portador:</span>
                      </td>
                      <td class="text-left" colspan="8">
                        <v-text-field
                          v-if="item.document_type === 'cpf'"
                          v-maska="$masks.cpf.opts.mask"
                          :model-value="item.document"
                          :rules="[validateCPF]"
                          variant="outlined"
                          density="compact"
                          hide-details
                          @maska="(e: CustomEvent<MaskaDetail>) => item.document = e.detail.unmasked"
                        />
                        <v-text-field
                          v-else
                          :model-value="item.document"
                          variant="outlined"
                          density="compact"
                          hide-details
                          @update:model-value="item.document = ($event ?? '').toUpperCase()"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td colspan="10">
                        <div class="d-flex justify-end pl-3">
                          <v-btn
                            class="mr-3"
                            color="primary"
                            :loading="loader.loading"
                            @click="closeHolderForm(item)"
                          >
                            Cancelar
                          </v-btn>
                          <v-btn
                            color="primary"
                            :loading="loader.loading"
                            @click="submitHolderPatch(item)"
                          >
                            Salvar
                          </v-btn>
                        </div>
                      </td>
                    </tr>
                  </template>
                </template>
              </tbody>
            </v-table>

            <span v-if="order?.coupons && order?.coupons.length > 0">
              <v-divider class="my-2" />

              <div class="my-6">
                <span class="text-h6 font-weight-medium"> Cupons</span>
              </div>

              <v-divider class="my-2" />

              <v-table>
                <thead>
                  <tr>
                    <th class="font-weight-bold text-left">
                      ID
                    </th>
                    <th class="font-weight-bold text-left">
                      Código
                    </th>
                    <th class="font-weight-bold text-left">
                      Método
                    </th>
                    <th class="font-weight-bold text-left">
                      Valor
                    </th>
                    <th class="font-weight-bold text-left">
                      Itens Aplicados
                    </th>
                    <th class="font-weight-bold text-left">
                      Desconto Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="coupon in order?.coupons" :key="coupon.id">
                    <td class="text-left">
                      {{ coupon.id }}
                    </td>
                    <td class="text-left">
                      {{ coupon.code }}
                    </td>
                    <td class="text-left">
                      {{ coupon.method === 'percentage' ? 'Porcentagem' : 'Absoluto' }}
                    </td>
                    <td class="text-left">
                      {{ coupon.method === 'percentage' ? `${coupon.value}%` : currency(coupon.value / 100) }}
                    </td>
                    <td class="text-left">
                      {{ getAppliedItemsCount(coupon, order?.items ?? []) }}
                    </td>
                    <td class="text-left">
                      {{ currency(getTotalDiscount(coupon, order?.items ?? [])) }}
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </span>

            <v-divider class="my-2" />

            <div v-if="order?.notifications" class="my-6">
              <span class="text-h6 font-weight-medium"> Notificações </span>
            </div>

            <v-divider v-if="order?.notifications" class="my-2" />

            <v-table v-if="order?.notifications">
              <thead>
                <tr>
                  <th class="font-weight-bold text-left">
                    Status do Pagamento
                  </th>
                  <th class="font-weight-bold text-left">
                    Data
                  </th>
                </tr>
              </thead>
              <tbody v-for="notification in order.notifications" :key="notification.id">
                <tr>
                  <td class="text-left">
                    {{ TransactionalStatusLabels[notification.status as TransactionalStatus] }}
                  </td>
                  <td class="text-left">
                    {{ $luxon.DateTime.fromISO(notification.created_at ?? '').toFormat('dd/MM/yyyy HH:mm:ss') }}
                  </td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog v-model="showDialog">
      <v-card>
        <v-toolbar
          dark
          color="primary"
          dense
          flat
        >
          <v-toolbar-title class="white--text">
            Dados Adquirente
          </v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <JsonViewer :data="order?.form_data ?? []" />
        </v-card-text>
        <v-card-actions class="pt-2">
          <v-spacer />
          <v-btn color="error" variant="flat" @click="showDialog = false">
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <dialog-change-ticket-type
      v-if="typeChangeItem && order"
      :item="typeChangeItem"
      :event-id="order.event_id"
      @close="typeChangeItem = undefined"
    />
  </section>
</template>
<script setup lang="ts">
import OrderService from '@/services/OrderService'
import AdminService from '@/services/AdminService'
import { useSnackBar } from '@/stores/snackbar'
import Order from '@/types/Order'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useLoader } from '@/stores/loader'
import TPageTitle from '@/components/TPageTitle.vue'
import { fromSQL } from '@/plugins/date'
import { formatDocument } from '@/helpers/formatters/document'
import { currency } from '@/helpers/formatters/currency'
import JsonViewer from '@/components/TJsonViewer.vue'
import { useAdminStore } from '@/stores/admin'
import { useDialog } from '@/stores/dialog'
import { TransactionalStatus, TransactionalStatusLabels } from '@/constants/TransactionalStatus'
import Reading from '@/types/Reading'
import Coupon from '@/types/Coupon'
import OrderItem from '@/types/OrderItem'
import { MaskaDetail } from 'maska'
import validateCPF from '@/helpers/rules/validateCPF'
import { useAbility } from '@casl/vue'
import { cellphone } from '@/helpers/formatters/cellphone'
import DialogChangeTicketType from './partials/dialog-change-ticket-type.vue'

const { can } = useAbility()
const route = useRoute()
const snackbar = useSnackBar()
const loader = useLoader()
const dialog = useDialog()

const order = ref<Order>()
const orderData = ref<{ text: string, value: string }[]>([])
const ownerData = ref<{ text: string, value: string }[]>([])

const loading = computed(() => loader.loading)

const refunded = computed(() => {
  if (!order.value) return false

  if (!order.value.notifications) return false

  return order.value.notifications.some(n => n.status === TransactionalStatus.REFUNDED)
})

const showDialog = ref(false)

onMounted(() => {
  fetch()
})

function print() {
  if (order.value?.id) {
    window.open(`${import.meta.env.VITE_API_URL}/api/admin/tickets/${order.value.id}/download`)
  }
}

function getReadingDate(reading: Reading | undefined) {
  if (!reading?.created_at) return '--'
  return fromSQL(reading.created_at, 'dd/MM/yyyy HH:mm:ss')
}

async function void_() {
  const confirm = await dialog.open({
    title: 'Estornar',
    message: 'Deseja mesmo estornar a compra? Essa é uma ação irreversível.',
  })

  if (!confirm) return

  try {
    loader.increment()
    await new OrderService().void(route.params.id as string)
    snackbar.show('Compra estornada com sucesso', 'success')
  } catch {
    snackbar.show('Erro ao estornar compra', 'error')
  } finally {
    loader.decrement()
  }
}

async function cancel() {
  const confirm = await dialog.open({
    title: 'Cancelar Pedido',
    message: 'Deseja mesmo cancelar esse pedido? Essa é uma ação irreversível.',
  })

  if (!confirm) return

  try {
    loader.increment()
    await new OrderService().cancel(route.params.id as string)
    await fetch()
    snackbar.show('Compra cancelada com sucesso', 'success')
  } catch {
    snackbar.show('Erro ao cancelar a compra', 'error')
  } finally {
    loader.decrement()
  }
}

async function chargeback() {
  const confirm = await dialog.open({
    title: 'Registrar Chargeback',
    message: 'Deseja registrar um chargeback para essa compra? Essa é uma ação irreversível.',
  })

  if (!confirm) return

  try {
    loader.increment()
    const response = await new OrderService().chargeback(route.params.id as string)
    if (order.value) {
      order.value.chargebacked_at = response.data.data.chargebacked_at
    }
    snackbar.show('Chargeback registrado com sucesso', 'success')
  } catch {
    snackbar.show('Erro ao registrar chargeback', 'error')
  } finally {
    loader.decrement()
  }
}

async function login() {
  try {
    loader.increment()
    if (!order.value?.owner?.id) {
      snackbar.show('Usuário não encontrado', 'error')
      return
    }
    const { data } = await new AdminService().getUserToken(useAdminStore().admin!.numeric_id, order.value?.owner?.id)
    const b64_token = btoa(JSON.stringify(data.data.token))
    window.open(`${import.meta.env.VITE_SALES_URL}/login?token=${b64_token}`, '_blank')
  } catch {
    snackbar.show('Erro ao tentar logar como administrador', 'error')
  } finally {
    loader.decrement()
  }
}

async function fetch() {
  try {
    loader.increment()
    const response = await new OrderService().show(route.params.id as string)
    order.value = response.data.data

    const transactionId = order.value?.form_data?.findLast(item => item.tid && item.tid !== '')?.tid ?? order.value.id

    orderData.value = [
      { text: 'Evento', value: order.value.event?.title ?? '--' },
      { text: 'Data do Evento', value: fromSQL(order.value.event?.dates?.start ?? '', 'dd/MM/yyyy HH:mm') },
      { text: 'Identificador da Compra', value: order.value.id },
      { text: 'Identificador da Transação', value: transactionId },
      { text: 'Situação da Compra', value: order.value.status },
      { text: 'Data da Compra', value: fromSQL(order.value.created_at, 'dd/MM/yyyy HH:mm:ss') },
      { text: 'Valor da Compra', value: currency(order.value.total_value) },
      { text: 'Método de Pagamento', value: order.value.payment_method?.name ?? '--' },
      { text: 'Parcelamento', value: `${order.value.installments}X` },
      { text: 'Processador de Pagamento', value: order.value.gateway?.name ?? '--' },
    ]

    ownerData.value = [
      { text: 'Nome', value: order.value.owner?.name ?? '--' },
      { text: 'Documento', value: formatDocument(order.value.owner?.document, order.value.owner?.document_type) },
      { text: 'E-mail', value: order.value.owner?.email ?? '--' },
      { text: 'Telefone', value: order.value.owner?.cellphone ? cellphone(order.value.owner?.cellphone) : '--' },
      { text: 'Data de Cadastro', value: fromSQL(order.value.owner?.created_at ?? '', 'dd/MM/yyyy HH:mm:ss') },
      { text: 'Data de Aniversário', value: fromSQL(order.value.owner?.birth_date ?? '', 'dd/MM/yyyy') },
    ]
  } catch {
    snackbar.show('Erro ao buscar detalhes da compra', 'error')
  } finally {
    loader.decrement()
  }
}

function getAppliedItemsCount(coupon: Coupon, items: OrderItem[]) {
  return getAppliedItems(coupon, items).length
}

function getTotalDiscount(coupon: Coupon, items: OrderItem[]) {
  return getAppliedItems(coupon, items)
    .reduce((acc, item) => acc + item.discount, 0)
}

function getAppliedItems(coupon: Coupon, items: OrderItem[]) {
  let appliedItems = []

  if (coupon.ticket_types && coupon.ticket_types.length > 0) {
    const allowedIds = coupon.ticket_types.map(tt => tt.id)
    appliedItems = items.filter(item =>
      item.passport?.ticket_type_id !== undefined && allowedIds.includes(item.passport.ticket_type_id),
    )
  } else {
    appliedItems = [ ...items ]
  }

  appliedItems.sort((a, b) => b.value - a.value)
  appliedItems = appliedItems.slice(0, coupon.max_items)

  return appliedItems
}

// Holders update (can be a more generalized order item update instead if we allow change of other data idk)

const originalItems = ref<OrderItem[]>([])

function getOriginalItem(item: OrderItem) {
  return originalItems.value.find(i => i.id === item.id)
}

function toggleHolderForm(item: OrderItem) {
  if (expanded(item.id)) {
    closeHolderForm(item)
  } else {
    originalItems.value.push({ ...item })
    expand(item.id)
  }
}

function closeHolderForm(item: OrderItem, commitChanges = false) {
  const original = getOriginalItem(item)

  if (!original) return

  originalItems.value = originalItems.value.filter(i => i.id !== item.id)

  if (!commitChanges) {
    item.name = original.name
    item.document = original.document
    item.document_type = original.document_type
  }

  collapse(item.id)
}

async function submitHolderPatch(item: OrderItem) {
  const confirm = await dialog.open({
    title: 'Confirmação de edição',
    message: 'Realmente deseja editar este portador?',
  })

  if (!confirm) return

  try {
    loader.increment()

    item.name = item.name.trim()

    await (new OrderService()).patchHolder(item.id, { name: item.name, document: item.document, document_type: item.document_type })

    closeHolderForm(item, true)

    snackbar.show('Portador atualizado.', 'success')
  } finally {
    loader.decrement()
  }
}

// Ticket type switch

const typeChangeItem = ref<OrderItem>()

// Expandable items

const expandedItemIds = ref<string[]>([])

function expand(itemId: string) {
  expandedItemIds.value.push(itemId)
}

function collapse(itemId: string) {
  expandedItemIds.value = expandedItemIds.value.filter(id => id !== itemId)
}

function expanded(itemId: string) {
  return expandedItemIds.value.some(id => id === itemId)
}

</script>
